import { useCallback, useEffect, useRef } from "react";

const WARNING_AFTER_MS = 5 * 60 * 1000;
const TIMEOUT_AFTER_MS = 15 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll"];

export default function useSessionActivity({
  endSession,
  heartbeat,
  lastHeartbeatRef,
  setShowWarning,
  user,
  userRef,
}) {
  const lastActivityRef = useRef(Date.now());

  const recordActivity = useCallback(() => {
    if (!userRef.current) return;
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    if (lastActivityRef.current - lastHeartbeatRef.current >= HEARTBEAT_INTERVAL_MS) {
      void heartbeat();
    }
  }, [heartbeat, lastHeartbeatRef, setShowWarning, userRef]);

  useEffect(() => {
    if (!user) return undefined;
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    const timeoutTimer = window.setInterval(() => {
      const inactiveFor = Date.now() - lastActivityRef.current;
      if (inactiveFor >= TIMEOUT_AFTER_MS) {
        void endSession();
        return;
      }
      if (inactiveFor >= WARNING_AFTER_MS) setShowWarning(true);
    }, 1000);
    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
      window.clearInterval(timeoutTimer);
    };
  }, [endSession, recordActivity, setShowWarning, user]);

  return { lastActivityRef, recordActivity };
}